import createClient from 'openapi-fetch'
import type { paths } from '@/api/schema'

/**
 * The ONE network client. Its types are generated from the backend's OpenAPI
 * document (`npm run gen:api`), so a backend change breaks the build, not a
 * screen at runtime. Screens never call fetch directly: they use the hooks in
 * src/api/<area>.ts, which call `api` and `unwrap`.
 */
export interface ApiErrorBody {
  error: { code: string; message: string; detail?: unknown }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
  get isUnauthenticated() {
    return this.status === 401
  }
  get isDenied() {
    return this.status === 403 && this.code === 'ACCESS_DENIED'
  }
  get isNotFound() {
    return this.status === 404
  }
  get needsStepUp() {
    return this.code === 'STEP_UP_REQUIRED'
  }
  get needsOrg() {
    return this.code === 'PRECONDITION_FAILED' && (this.detail as { reason?: string } | undefined)?.reason === 'no_org'
  }
  get isUnavailable() {
    return this.status === 0 || this.status >= 500
  }
  /** Field errors from a 400 with zod details, keyed by field name. */
  get fieldErrors(): Record<string, string[]> {
    const d = this.detail as { fields?: Record<string, string[]> } | undefined
    return d?.fields ?? {}
  }
}

/** Cross-cutting reactions (session expiry, step-up prompt) subscribe here. */
type Listener = (e: ApiError) => void
const listeners = new Set<Listener>()
export function onApiError(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export const api = createClient<paths>({ baseUrl: '', credentials: 'include' })

type Result<T> = { data?: T; error?: unknown; response: Response }

/** Turn an openapi-fetch result into data, or throw an ApiError. */
export async function unwrap<T>(p: Promise<Result<T>>): Promise<T> {
  let r: Result<T>
  try {
    r = await p
  } catch {
    const e = new ApiError(0, 'NETWORK', 'Can’t reach ESM. Check your connection and try again.')
    listeners.forEach(l => l(e))
    throw e
  }
  if (r.response.ok) return r.data as T
  const body = r.error as Partial<ApiErrorBody> | undefined
  const e = new ApiError(
    r.response.status,
    body?.error?.code ?? `HTTP_${r.response.status}`,
    body?.error?.message ?? (r.response.status >= 500 ? 'Something went wrong on our side. Try again in a moment.' : 'The request could not be completed.'),
    body?.error?.detail,
  )
  listeners.forEach(l => l(e))
  throw e
}

/** JSON fetch for endpoints outside the generated document (downloads, BetterAuth extras). */
export async function rawJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, { credentials: 'include', ...init, headers: { 'content-type': 'application/json', ...(init.headers ?? {}) } })
  } catch {
    throw new ApiError(0, 'NETWORK', 'Can’t reach ESM. Check your connection and try again.')
  }
  const text = await res.text()
  let body: unknown
  try {
    body = text ? JSON.parse(text) : undefined
  } catch {
    body = undefined
  }
  if (!res.ok) {
    const b = body as { error?: { code?: string; message?: string; detail?: unknown }; code?: string; message?: string } | undefined
    throw new ApiError(res.status, b?.error?.code ?? b?.code ?? `HTTP_${res.status}`, b?.error?.message ?? b?.message ?? 'The request could not be completed.', b?.error?.detail)
  }
  return body as T
}

/** Message for any thrown value, for toasts and inline errors. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'Something went wrong.'
}
